// bulletinDesigns — the design words an operator picks for a window note or a Shop banner.
//
// One catalogue for the whole Shop page, mirrored from two places that must agree with it:
//   • sql/095_bulletin_designs_and_shop_banners.sql — the CHECK constraints. Every key offered
//     here is a value the database accepts; nothing else is offered.
//   • the customer app's NoteDesign (scoopcrm-customer-ios Scooper/Core/Bulletins.swift) — the
//     icon, colour and font keys and the paper/ink pairs. The CSS here is a preview of what the
//     app draws, close enough to choose by, not a copy of it.
// A new deal is a new ROW in app_bulletins, never an app build — so this page is where a deal is
// "built". Adding a look still needs both of the above to learn the new key first.

export const LIMITS = { title: 60, body: 160, cta: 24, stamp: 14, link: 300, tiltMin: -12, tiltMax: 12 };

// customer_bulletins(p_placement) hands the app at most this many live rows, in order.
export const CAPACITY = { window: 4, shop: 6 };

export const KINDS = [
  ["announcement", "Announcement"],
  ["offer", "Offer"],
  ["deal", "Deal"],
  ["shop_local", "Shop local"],
  ["event", "Event"],
  ["coming_soon", "Coming soon"],
];
export const KIND_LABEL = Object.fromEntries(KINDS);

// The window's papers. The first five are the originals (sql/091); the app picks one of those
// itself when the operator leaves the paper blank.
export const WINDOW_STYLES = [
  ["paper", "Notebook page"], ["sticky", "Sticky note"], ["index", "Index card"], ["kraft", "Kraft paper"],
  ["flyer", "Tear-off flyer"], ["coupon", "Coupon"], ["ticket", "Ticket"], ["polaroid", "Polaroid"],
  ["chalkboard", "Chalkboard"], ["postcard", "Postcard"], ["gifttag", "Gift tag"], ["receipt", "Receipt"],
  ["starburst", "Starburst"], ["pennant", "Pennant"], ["envelope", "Envelope"], ["speech", "Speech bubble"],
  ["heart", "Heart"],
];
// The Shop's banner looks — in the app's navy and gold.
export const SHOP_STYLES = [
  ["hero", "Hero"], ["gold", "Gold"], ["glass", "Frosted glass"], ["tile", "Tile"], ["ribbon", "Ribbon"], ["spotlight", "Spotlight"],
];
export const STYLE_LABEL = Object.fromEntries([...WINDOW_STYLES, ...SHOP_STYLES]);

export const FASTENERS = [
  ["tape", "Masking tape"], ["washi", "Washi tape"], ["pin", "Push pin"], ["magnet", "Magnet"],
  ["suction", "Suction hook"], ["clip", "Binder clip"], ["paperclip", "Paper clip"], ["staple", "Staple"],
  ["bandage", "Band-aid"], ["gum", "Bubble gum"], ["twine", "Twine"], ["clothespin", "Clothespin"],
  ["sticker", "Sticker"], ["none", "Nothing"],
];
export const FASTENER_LABEL = Object.fromEntries(FASTENERS);

// Paper (or banner) fill + the ink the app writes on it. Same pairs as NoteDesign.colors, each
// ≥ 4.5:1. Red is #BC2A31 (white on it 6.0:1) — the same value as the app.
export const COLORS = [
  ["white", "White", "#FBFAF6", "#1B2A4A"], ["cream", "Cream", "#F6EED8", "#2B2418"],
  ["yellow", "Yellow", "#FBE68A", "#2E2608"], ["pink", "Pink", "#F9C9D6", "#4A1426"],
  ["blue", "Blue", "#C9DDF7", "#0E2A55"], ["green", "Green", "#CDE8C4", "#163A16"],
  ["mint", "Mint", "#CFF1E4", "#0F3B2E"], ["orange", "Orange", "#FBD1A6", "#4A2408"],
  ["purple", "Purple", "#DCCFF5", "#2C1650"], ["red", "Red", "#BC2A31", "#FFFFFF"],
  ["navy", "Navy", "#072C76", "#FFFFFF"], ["gold", "Gold", "#F4C120", "#072C76"],
  ["kraft", "Kraft", "#C9A274", "#2A1A0A"],
];
export const COLOR = Object.fromEntries(COLORS.map(([k, label, paper, ink]) => [k, { label, paper, ink }]));

// The app uses fonts that ship with iOS. A Mac browser has the same faces; elsewhere the
// stacks fall back to the nearest cousin, which is why the preview says it's a preview.
export const FONTS = [
  ["marker", "Marker", `"Marker Felt", "Comic Sans MS", "Chalkboard SE", cursive`, `"Marker Felt", "Comic Sans MS", cursive`, 700],
  ["chalk", "Chalk", `"Chalkboard SE", "Chalkboard", "Comic Sans MS", cursive`, `"Chalkboard SE", "Chalkboard", "Comic Sans MS", cursive`, 700],
  ["hand", "Handwritten", `"Noteworthy", "Bradley Hand", "Segoe Print", cursive`, `"Noteworthy", "Bradley Hand", "Segoe Print", cursive`, 700],
  ["typewriter", "Typewriter", `"American Typewriter", "Courier New", Courier, monospace`, `"American Typewriter", "Courier New", Courier, monospace`, 700],
  ["bold", "Bold", `"Avenir Next", "Arial Black", "Helvetica Neue", sans-serif`, `"Avenir Next", "Helvetica Neue", Arial, sans-serif`, 800],
  ["script", "Script", `"Snell Roundhand", "Brush Script MT", "Segoe Script", cursive`, `"Snell Roundhand", "Brush Script MT", "Segoe Script", cursive`, 700],
];
export const FONT = Object.fromEntries(FONTS.map(([k, label, title, body, weight]) => [k, { label, title, body, weight }]));

// Keyword → a stand-in glyph for the preview. The app draws its own symbol for each keyword
// (NoteDesign.icons); the DB accepts any ^[a-z]{1,20}$, but only these are offered because only
// these draw anything.
export const ICONS = [
  ["paw", "🐾"], ["dog", "🐶"], ["cat", "🐱"], ["ball", "🎾"], ["gift", "🎁"], ["tag", "🏷️"], ["percent", "%"],
  ["star", "⭐"], ["heart", "❤️"], ["leaf", "🍃"], ["snowflake", "❄️"], ["sun", "☀️"], ["bell", "🔔"],
  ["store", "🏪"], ["sparkles", "✨"], ["calendar", "📅"], ["megaphone", "📣"], ["scissors", "✂️"],
  ["house", "🏠"], ["treat", "🍴"], ["bath", "🛁"], ["truck", "🚚"], ["trophy", "🏆"], ["party", "🎉"],
  ["flag", "🚩"], ["cart", "🛒"], ["ticket", "🎟️"], ["clock", "🕐"],
];
export const ICON = Object.fromEntries(ICONS);

// Screens a tap can open (sql/095 link_chk), or "web" for an https:// page.
export const LINKS = [["", "Nothing"], ["shop", "Shop"], ["plan", "Plan"], ["tips", "Tips"], ["visits", "Visits"], ["settings", "Settings"], ["web", "A web page…"]];
export const SCREENS = ["shop", "plan", "tips", "visits", "settings"];
export const LINK_LABEL = Object.fromEntries(LINKS);

export const STAMP_IDEAS = ["NEW", "SALE", "FREE", "SOON", "10% OFF", "LIMITED"];

const HTTPS = /^https:\/\/[^\s]+$/;

// ── drafts ───────────────────────────────────────────────────────────
// A draft is the editor's working copy: strings where the DB has nulls, dates as the
// <input type="datetime-local"> wants them. rowFrom/draftFrom are the only two converters.

const pad = (n) => String(n).padStart(2, "0");
export function toLocalInput(ts) {
  if (!ts) return "";
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return "";
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
const fromLocalInput = (s) => {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

export function blankDraft(placement) {
  return {
    title: "", body: "", kind: placement === "shop" ? "deal" : "announcement",
    style: placement === "shop" ? "hero" : "", fastener: "", tilt: null,
    cta: "", action: "none", active: true, starts: "", ends: "",
    color: "", font: "", icon: "", stamp: "", linkKind: "", linkUrl: "",
  };
}

export function draftFrom(b) {
  const link = b.link || "";
  const isScreen = SCREENS.includes(link);
  return {
    title: b.title || "", body: b.body || "", kind: b.kind || "announcement",
    style: b.style || "", fastener: b.fastener || "",
    tilt: b.tilt == null || b.tilt === "" ? null : Number(b.tilt),
    cta: b.cta || "", action: b.action === "claim" ? "claim" : "none", active: b.active !== false,
    starts: toLocalInput(b.starts_at), ends: toLocalInput(b.ends_at),
    color: b.color || "", font: b.font || "", icon: b.icon || "", stamp: b.stamp || "",
    linkKind: !link ? "" : isScreen ? link : "web", linkUrl: !link || isScreen ? "" : link,
  };
}

// What the app would receive for this draft — the preview draws from this, so it shows the
// same trimmed, nulled values the database will hold.
export function rowFrom(d, placement) {
  const t = (s) => { const v = String(s ?? "").trim(); return v === "" ? null : v; };
  const link = d.linkKind === "web" ? t(d.linkUrl) : (d.linkKind || null);
  const shop = placement === "shop";
  return {
    placement,
    title: String(d.title || "").trim(),
    body: t(d.body),
    kind: d.kind,
    style: t(d.style),
    // A banner isn't hung on anything and doesn't lean.
    fastener: shop ? null : t(d.fastener),
    tilt: shop || d.tilt == null ? null : Math.round(Number(d.tilt)),
    cta: d.action === "claim" ? t(d.cta) : null,
    action: d.action === "claim" ? "claim" : "none",
    active: !!d.active,
    starts_at: fromLocalInput(d.starts),
    ends_at: fromLocalInput(d.ends),
    color: t(d.color), font: t(d.font), icon: t(d.icon), stamp: t(d.stamp),
    link,
  };
}

// The DB's limits, checked here first so a save never comes back as a constraint name.
// Returns { errors: {field: message}, warnings: [message] } — errors block a save, warnings don't.
export function validate(d, placement, now = Date.now()) {
  const r = rowFrom(d, placement);
  const errors = {};
  const warnings = [];
  if (!r.title) errors.title = "A note needs a title.";
  else if (r.title.length > LIMITS.title) errors.title = `Keep the title to ${LIMITS.title} characters.`;
  if (r.body && r.body.length > LIMITS.body) errors.body = `Keep the message to ${LIMITS.body} characters.`;
  if (!KIND_LABEL[r.kind]) errors.kind = "Pick what this note is.";
  if (r.cta && r.cta.length > LIMITS.cta) errors.cta = `Keep the button to ${LIMITS.cta} characters.`;
  if (r.stamp && r.stamp.length > LIMITS.stamp) errors.stamp = `Keep the stamp to ${LIMITS.stamp} characters.`;
  const styles = placement === "shop" ? SHOP_STYLES : WINDOW_STYLES;
  if (r.style && !styles.some(([k]) => k === r.style)) errors.style = "That look isn't available here.";
  if (r.fastener && !FASTENER_LABEL[r.fastener]) errors.fastener = "Pick a fastener from the list.";
  if (r.color && !COLOR[r.color]) errors.color = "Pick a color from the list.";
  if (r.font && !FONT[r.font]) errors.font = "Pick a handwriting from the list.";
  if (r.icon && !ICON[r.icon]) errors.icon = "Pick a picture from the list.";
  if (r.tilt != null && (r.tilt < LIMITS.tiltMin || r.tilt > LIMITS.tiltMax)) errors.tilt = "Tilt is between −12° and 12°.";
  if (d.linkKind === "web") {
    if (!r.link) errors.link = "Paste the page's address, starting with https://";
    else if (!HTTPS.test(r.link)) errors.link = "Web links must start with https:// and have no spaces.";
    else if (r.link.length > LIMITS.link) errors.link = `That address is longer than ${LIMITS.link} characters.`;
  }
  if (d.starts && !r.starts_at) errors.starts = "That start date isn't a real date.";
  if (d.ends && !r.ends_at) errors.ends = "That end date isn't a real date.";
  if (r.starts_at && r.ends_at && Date.parse(r.ends_at) <= Date.parse(r.starts_at)) errors.ends = "It has to end after it starts.";

  if (r.active && r.ends_at && Date.parse(r.ends_at) <= now) warnings.push("This end date has already passed, so clients won't see it.");
  if (r.kind === "coming_soon" && r.action === "claim") warnings.push("A Claim button on a “coming soon” note invites claims for something that isn't ready yet.");
  if ((r.kind === "offer" || r.kind === "deal") && r.active) warnings.push("Clients will read this as a real offer — only post one you'll honor.");
  return { errors, warnings, row: r };
}

// ── status & order ───────────────────────────────────────────────────

// customer_bulletins: ORDER BY sort, created_at DESC.
export function ordered(rows) {
  return rows.slice().sort((a, b) =>
    (Number(a.sort) || 0) - (Number(b.sort) || 0) || String(b.created_at || "").localeCompare(String(a.created_at || "")));
}

export function statusOf(b, now = Date.now()) {
  if (!b.active) return "off";
  if (b.starts_at && Date.parse(b.starts_at) > now) return "scheduled";
  if (b.ends_at && Date.parse(b.ends_at) <= now) return "ended";
  return "live";
}

// The live rows the app actually receives right now: the first CAPACITY, in order.
export function onScreen(rows, placement, now = Date.now()) {
  return ordered(rows).filter((b) => statusOf(b, now) === "live").slice(0, CAPACITY[placement]);
}

export const shortDate = (ts) =>
  ts ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

// PostgREST answers a CHECK failure with the constraint's name — say it in words.
export function saveError(text = "") {
  const t = String(text);
  if (/title_len/.test(t)) return `Title has to be 1–${LIMITS.title} characters.`;
  if (/body_len/.test(t)) return `Message has to be ${LIMITS.body} characters or fewer.`;
  if (/cta_len/.test(t)) return `Button words have to be ${LIMITS.cta} characters or fewer.`;
  if (/stamp_len/.test(t)) return `Stamp has to be 1–${LIMITS.stamp} characters.`;
  if (/link_chk/.test(t)) return "That link isn't a screen in the app or an https:// page.";
  if (/tilt_chk/.test(t)) return "Tilt has to be between −12° and 12°.";
  if (/_chk/.test(t)) return "The server didn't accept one of the design choices — the app may not know it yet.";
  if (/row-level security|42501|permission/i.test(t)) return "You don't have permission to change these.";
  return "Couldn't save that. Nothing changed.";
}
